import type { ReactElement, ReactNode } from "react";

import { Avatar, type AvatarProps } from "@/components/Atoms/Avatar/Avatar";
import { AccordionItem } from "@/components/Molecules/AccordionItem/AccordionItem";
import { cx } from "@/utils/cx";

export interface ActivityItemProps {
	/** Event label shown as the accordion title (e.g. "Updated Ticket"). */
	primaryText: ReactNode;
	/** Author and timestamp (e.g. "Sara Chen • Apr 7, 2026"). */
	secondaryText?: ReactNode;
	/**
	 * Whether to show the dashed vertical connector under the avatar.
	 * Set `false` on the last item in an ActivityList.
	 * @default true
	 */
	showConnector?: boolean;
	/** Avatar image URL. */
	avatarSrc?: AvatarProps["src"];
	/** Accessible name for the avatar. */
	avatarAlt?: string;
	/** Initials fallback when no image is available. */
	avatarInitials?: string;
	/** Controlled open state for the nested accordion. */
	isExpanded?: boolean;
	/**
	 * Initial open state when uncontrolled.
	 * @default false
	 */
	defaultExpanded?: boolean;
	/** Fires when the accordion expands or collapses. */
	onExpandedChange?: (expanded: boolean) => void;
	/** Content shown inside the accordion when expanded. */
	children?: ReactNode;
	className?: string;
	/** Optional test id override. */
	"data-testid"?: string;
}

/**
 * Single row in an ActivityList timeline — avatar + optional connector on the
 * left, AccordionItem on the right.
 *
 * @see Figma ActivityItem 4376:24909
 */
export function ActivityItem({
	primaryText,
	secondaryText,
	showConnector = true,
	avatarSrc,
	avatarAlt = "User avatar",
	avatarInitials,
	isExpanded,
	defaultExpanded = false,
	onExpandedChange,
	children,
	className,
	"data-testid": dataTestId = "activity-item",
}: ActivityItemProps): ReactElement {
	return (
		<div
			className={cx("flex items-start gap-1", className)}
			data-testid={dataTestId}
		>
			<div className="flex w-8 shrink-0 flex-col items-center gap-3 self-stretch">
				<Avatar
					size="sm"
					src={avatarSrc}
					alt={avatarAlt}
					initials={avatarInitials}
				/>
				{showConnector && (
					<div
						aria-hidden
						className="relative my-0 flex min-h-2 w-full flex-1 justify-center"
					>
						<svg className="absolute inset-x-0 top-0 h-full w-full" width="2.4">
							<title>Connector</title>
							<line
								x1="50%"
								y1="0"
								x2="50%"
								y2="100%"
								className="stroke-border-primary"
								strokeWidth="2.4"
								strokeDasharray="0,6"
								strokeLinecap="round"
							/>
						</svg>
					</div>
				)}
			</div>

			<AccordionItem
				className="min-w-0 flex-1"
				size="sm"
				showBorder
				showBadge={false}
				showLeadingSlot={false}
				showSecondaryText={secondaryText != null && secondaryText !== ""}
				truncatePrimaryText
				primaryText={primaryText}
				secondaryText={secondaryText}
				isExpanded={isExpanded}
				defaultExpanded={defaultExpanded}
				onExpandedChange={onExpandedChange}
			>
				{children != null ? (
					<div className="min-h-[72px] w-full rounded-lg bg-disabled p-3">
						{children}
					</div>
				) : null}
			</AccordionItem>
		</div>
	);
}
